import { InformationCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import {
  Component,
  ErrorInfo,
  ReactNode,
  Suspense,
  useEffect,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';

import { AppRouteBody } from '@/components/AppRouteBody';
import { Tabs } from '@/components/chariot-ui';
import { Spinner } from '@/components/ui/spinner';
import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useGetSubscription } from '@/hooks/useSubscription';
import { Monitoring } from '@/sections/settings/monitoring';
import { Subscription } from '@/sections/settings/subscription';
import { AccessLogsTab } from '@/sections/settings/tabs/AccessLogsTab';
import { NotificationsTab } from '@/sections/settings/tabs/NotificationsTab';
import { OrganizationTab } from '@/sections/settings/tabs/OrganizationTab';
import { ScanSettingsTab } from '@/sections/settings/tabs/ScanSettingsTab';
import { UserManagementTab } from '@/sections/settings/tabs/UserManagementTab';
import { useAuth } from '@/state/auth';
import { SubscriptionConfiguration } from '@/types';
import { QueryStatus } from '@/utils/api';

/**
 * Settings page tabs configuration.
 * Static configuration - no need for useMemo, React Compiler handles optimization.
 */
const SETTINGS_TABS = [
  { label: 'Scanning', value: 'scanSettings' },
  { label: 'Notifications', value: 'notificationSettings' },
  { label: 'Organization', value: 'orgSettings' },
  { label: 'Users', value: 'userManagement' },
  { label: 'Access Logs', value: 'accessLogs' },
  { label: 'Monitoring', value: 'monitoring' },
  { label: 'Subscription', value: 'subscription' },
];

/**
 * ErrorBoundary component for catching and displaying React errors.
 * Used to wrap Suspense boundaries to provide graceful error handling.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Settings tab error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

/**
 * SubscriptionTabContent handles permissions and content display for the subscription tab.
 *
 * Loading states are managed by parent Suspense boundary.
 * This component only renders when data is available or shows permission errors.
 *
 * Permissions logic:
 * - Show content if subscription data exists OR user is Praetorian user
 * - Show "No Access" message if no subscription data and not Praetorian user
 */
interface SubscriptionTabContentProps {
  subscriptionStatus: QueryStatus;
  subscriptionValue: SubscriptionConfiguration | undefined;
  isPraetorianUser: boolean;
  email?: string;
}

function SubscriptionTabContent({
  subscriptionStatus,
  subscriptionValue,
  isPraetorianUser,
  email,
}: SubscriptionTabContentProps) {
  // Fetch monitoring counts for subscription display
  const { data: monitoringCounts, status: monitoringCountsStatus } =
    useGetMonitoringCounts({
      email,
      countType: 'active_monitoring_count',
    });

  // If still loading, return null to let Suspense handle the loading state
  if (
    subscriptionStatus === 'pending' ||
    monitoringCountsStatus === 'pending'
  ) {
    return null;
  }

  // Permission check - show appropriate message if no access
  const hasSubscriptionAccess =
    subscriptionValue !== undefined || isPraetorianUser;

  if (!hasSubscriptionAccess) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <InformationCircleIcon className="text-chariot-text-secondary size-12" />
        <div className="flex flex-col gap-2 text-center">
          <h2 className="text-chariot-text-primary text-lg font-bold">
            No Subscription Access
          </h2>
          <p className="text-chariot-text-secondary max-w-md text-sm">
            Your account does not have access to subscription management.
            Contact your administrator or Praetorian support for assistance.
          </p>
        </div>
      </div>
    );
  }

  // Render actual subscription content
  return (
    <Subscription
      subscriptionValue={subscriptionValue}
      monitoringCounts={monitoringCounts}
    />
  );
}

interface SettingsProps {
  defaultTab?: string;
}

export function Settings({ defaultTab }: SettingsProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isImpersonating, friend, isPraetorianUser } = useAuth();

  const { data: subscriptionValue, status: subscriptionStatus } =
    useGetSubscription({ email: isImpersonating ? friend : undefined });

  // Use defaultTab if provided (from route), otherwise use first tab
  const [selectedTab, setSelectedTab] = useState<string>(
    defaultTab || SETTINGS_TABS[0].value
  );

  // CRITICAL: Sync state with URL when defaultTab prop changes
  // This ensures navigation between different settings routes correctly updates the tab
  useEffect(() => {
    if (defaultTab && defaultTab !== selectedTab) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Syncing URL-derived prop to local state; useEffect is the correct pattern for external state sync
      setSelectedTab(defaultTab);
    }
  }, [defaultTab]);

  return (
    <AppRouteBody title="Settings" data-tutorial="settings-section">
      {/* Tabs */}

      <Tabs
        className="mb-12"
        tabs={SETTINGS_TABS}
        variant="primary"
        selected={
          SETTINGS_TABS.find(tab => tab.value === selectedTab) ||
          SETTINGS_TABS[0]
        }
        onSelect={tab => {
          setSelectedTab(tab.value);
          // Navigate to the appropriate route based on the selected tab
          const pathPrefix = location.pathname.split('/settings')[0];
          const tabRouteMap: Record<string, string> = {
            scanSettings: `${pathPrefix}/settings/scan`,
            notificationSettings: `${pathPrefix}/settings/notifications`,
            orgSettings: `${pathPrefix}/settings/organization`,
            userManagement: `${pathPrefix}/settings/user-management`,
            accessLogs: `${pathPrefix}/settings/access-logs`,
            monitoring: `${pathPrefix}/settings/monitoring`,
            subscription: `${pathPrefix}/settings/subscription`,
          };
          // Default to base settings route if tab not mapped
          const newPath = tabRouteMap[tab.value] || `${pathPrefix}/settings`;
          navigate(newPath, { replace: true });
        }}
      />

      <div className="flex h-full flex-col gap-14 overflow-auto pr-4">
        {selectedTab === 'scanSettings' && <ScanSettingsTab />}
        {selectedTab === 'notificationSettings' && <NotificationsTab />}
        {selectedTab === 'monitoring' && (
          <ErrorBoundary
            fallback={
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <XCircleIcon className="text-chariot-status-critical size-12" />
                <div className="flex flex-col gap-2 text-center">
                  <h2 className="text-chariot-text-primary text-lg font-bold">
                    Failed to Load Monitoring Data
                  </h2>
                  <p className="text-chariot-text-secondary max-w-md text-sm">
                    Unable to retrieve monitoring data. Please refresh the page
                    or contact support.
                  </p>
                </div>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="flex h-full flex-col items-center justify-center gap-4">
                  <Spinner size="xl" />
                  <p className="text-chariot-text-secondary text-sm">
                    Loading monitoring data...
                  </p>
                </div>
              }
            >
              <Monitoring email={isImpersonating ? friend : undefined} />
            </Suspense>
          </ErrorBoundary>
        )}
        {selectedTab === 'subscription' && (
          <ErrorBoundary
            fallback={
              <div className="flex h-full flex-col items-center justify-center gap-4">
                <XCircleIcon className="text-chariot-status-critical size-12" />
                <div className="flex flex-col gap-2 text-center">
                  <h2 className="text-chariot-text-primary text-lg font-bold">
                    Failed to Load Subscription
                  </h2>
                  <p className="text-chariot-text-secondary max-w-md text-sm">
                    Unable to retrieve subscription data. Please refresh the
                    page or contact support.
                  </p>
                </div>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="flex h-full flex-col items-center justify-center gap-4">
                  <Spinner size="xl" />
                  <p className="text-chariot-text-secondary text-sm">
                    Loading subscription data...
                  </p>
                </div>
              }
            >
              <SubscriptionTabContent
                subscriptionStatus={subscriptionStatus}
                subscriptionValue={subscriptionValue}
                isPraetorianUser={isPraetorianUser}
                email={isImpersonating ? friend : undefined}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {selectedTab === 'orgSettings' && <OrganizationTab />}
        {selectedTab === 'userManagement' && <UserManagementTab />}
        {selectedTab === 'accessLogs' && <AccessLogsTab />}
      </div>
    </AppRouteBody>
  );
}
